import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Empty,
    Modal,
    Row,
    Skeleton,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    DollarOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    LinkOutlined,
    LoadingOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SearchOutlined,
    StopOutlined,
    EditOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { ModelBeliefsAdhocForm } from './ModelBeliefsAdhocForm';
import { ModelBeliefsBrandPicker } from './ModelBeliefsBrandPicker';
import { ModelBeliefsBrandConfigForm } from './ModelBeliefsBrandConfigForm';
import { ModelBeliefsRunDetail } from './ModelBeliefsRunDetail';
import { ModelBeliefsReadPreview } from './ModelBeliefsReadPreview';
import { ModelBeliefsRunModal, ModelBeliefsRunTarget } from './ModelBeliefsRunModal';
import {
    aiAdminApi,
    ModelBeliefsPanel,
    ModelBeliefsRunCost,
    ModelBeliefsRunSummary,
    ModelBeliefsRunsResponse,
} from '../../../services/ai-admin-api';

const { Text, Paragraph } = Typography;

const STATUS_COLOUR: Record<string, string> = {
    READY: 'green',
    SAMPLING: 'blue',
    GATING: 'blue',
    AGGREGATING: 'blue',
    SYNTHESISING: 'blue',
    QUEUED: 'gold',
    FAILED: 'red',
    CANCELLED: 'default',
    INSUFFICIENT_DATA: 'orange',
};

// Null-safe on purpose. A column bound to a field the API no longer returns
// crashed every row of the table rather than showing one blank cell, which is
// a poor trade for a cost figure.
const usd = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(4)}`;
};

const when = (value: string | null): string =>
    value ? new Date(value).toLocaleString() : '-';

// Operational view: what ran, what it cost, what it found. Deliberately NOT
// the Configure tab — every other subsystem's Configure edits prompts and
// settings, and the draft/version machinery hangs off that contract. Runs are
// a different question and get their own tab.
export const ModelBeliefsRunsPanel: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [runs, setRuns] = useState<ModelBeliefsRunsResponse | null>(null);
    const [panel, setPanel] = useState<ModelBeliefsPanel | null>(null);
    const [costs, setCosts] = useState<Record<number, ModelBeliefsRunCost>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Starting a read: pick a brand, then estimate and start; or the ad-hoc
    // form for a brand that is not on the platform.
    const [pickingBrand, setPickingBrand] = useState(false);
    const [pickedBrand, setPickedBrand] = useState<{ brandId: number; name: string } | null>(null);
    const [runTarget, setRunTarget] = useState<ModelBeliefsRunTarget | null>(null);
    const [adhocOpen, setAdhocOpen] = useState(false);
    // Attaching an ad-hoc read to a brand that has since signed up.
    // The ad-hoc target behind a run, as a config row. A run from before
    // targets had configs gets one made from its stored target first.
    const [editingTarget, setEditingTarget] = useState<{ configId: number; name: string } | null>(null);
    const editTarget = async (row: ModelBeliefsRunSummary) => {
        if (!jwt) return;
        try {
            const configId =
                row.configId ?? (await aiAdminApi.adhocConfigForModelBeliefsRun(jwt, row.id)).configId;
            setEditingTarget({ configId, name: row.targetName ?? `Run #${row.id}` });
        } catch (e) {
            message.error((e as Error).message);
        }
    };
    const [attaching, setAttaching] = useState<ModelBeliefsRunSummary | null>(null);
    const [attachBrand, setAttachBrand] = useState<{ brandId: number; name: string } | null>(null);
    const [attachBusy, setAttachBusy] = useState(false);

    const load = useCallback(async () => {
        if (!jwt) return;
        setLoading(true);
        setError(null);
        try {
            const [runsResponse, panelResponse] = await Promise.all([
                aiAdminApi.listModelBeliefsRuns(jwt, { limit: 25 }),
                aiAdminApi.getModelBeliefsPanel(jwt),
            ]);
            setRuns(runsResponse);
            setPanel(panelResponse);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, [jwt]);

    useEffect(() => {
        void load();
    }, [load]);

    // While a run is working, or an admin page read is, the table refreshes
    // itself; otherwise a row sits on a stale stage until someone presses
    // refresh.
    useEffect(() => {
        const busy = (runs?.runs ?? []).some(
            (run) => run.pageReading === true || !['READY', 'FAILED', 'CANCELLED'].includes(run.status),
        );
        if (!busy) return undefined;
        const timer = window.setTimeout(() => void load(), 15000);
        return () => window.clearTimeout(timer);
    }, [runs, load]);

    // Costs are fetched per run rather than with the list: each one joins the
    // token rows, and a table of 25 does not need 25 joins to render.
    const loadCost = useCallback(
        async (runId: number) => {
            if (!jwt || costs[runId]) return;
            try {
                const cost = await aiAdminApi.getModelBeliefsRunCost(jwt, runId);
                setCosts((prev) => ({ ...prev, [runId]: cost }));
            } catch {
                // A missing cost is not worth breaking the table for: runs
                // from before usage recording landed have no rows at all.
            }
        },
        [jwt, costs],
    );

    const cancel = async (runId: number) => {
        if (!jwt) return;
        await aiAdminApi.cancelModelBeliefsRun(jwt, runId);
        await load();
    };

    const attach = async () => {
        if (!jwt || !attaching || !attachBrand) return;
        setAttachBusy(true);
        try {
            const result = await aiAdminApi.attachModelBeliefsRun(jwt, attaching.id, attachBrand.brandId);
            message.success(
                `Run #${attaching.id} is now ${attachBrand.name}'s read` +
                    (result.configCreated ? '; the brand\u2019s Model Beliefs config was created from it' : ''),
            );
            setAttaching(null);
            setAttachBrand(null);
            await load();
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setAttachBusy(false);
        }
    };

    if (loading && !runs) return <Skeleton active paragraph={{ rows: 8 }} />;
    if (error) return <Alert type="error" message={error} showIcon />;

    const live = (runs?.runs ?? []).filter((run) =>
        ['QUEUED', 'SAMPLING', 'GATING', 'AGGREGATING', 'SYNTHESISING'].includes(
            run.status,
        ),
    );

    const columns = [
        {
            title: 'Run',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            render: (id: number) => <Text strong style={{ whiteSpace: 'nowrap' }}>#{id}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 190,
            render: (status: string, row: ModelBeliefsRunSummary) => (
                <Space direction="vertical" size={0}>
                    <Tag color={STATUS_COLOUR[status] ?? 'default'}>{status}</Tag>
                    {row.resumeAfter && (
                        // A run waiting on a daily quota looks identical to a
                        // stuck one unless it says so.
                        <Tooltip title="Waiting on a provider's daily quota. It resumes itself; nothing already paid for is paid again.">
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                resumes {when(row.resumeAfter)}
                            </Text>
                        </Tooltip>
                    )}
                    {!row.resumeAfter && row.pageReading && (
                        <Tooltip title="An admin page read is working on this run. It re-writes the read when it finishes.">
                            <Text style={{ fontSize: 12, color: '#fa8c16' }}>
                                <LoadingOutlined style={{ marginRight: 4 }} />
                                reading pages
                            </Text>
                        </Tooltip>
                    )}
                    {!row.resumeAfter && !row.pageReading && row.stage && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.stage}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Brand',
            dataIndex: 'brandId',
            key: 'brandId',
            width: 110,
            render: (brandId: number | null, row: ModelBeliefsRunSummary) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.targetName ?? (brandId ? `Brand ${brandId}` : 'Ad hoc')}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {brandId ? `brand ${brandId}` : <Tag style={{ margin: 0 }}>ad-hoc</Tag>}
                        {row.marketCountry ? ` · ${row.marketCountry}` : ''}
                    </Text>
                </Space>
            ),
        },
        {
            title: (
                <Space size={4}>
                    Calls
                    <Tooltip title="Statement calls against the budget, which governs those only. The awareness gate is planned separately and its calls are recorded against the same run, so a combined figure reads as a budget overrun that never happened.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </Space>
            ),
            key: 'calls',
            width: 110,
            render: (_: unknown, row: ModelBeliefsRunSummary) => {
                const statements = row.callsByKind?.statements ?? null;
                const gate = row.callsByKind?.gate ?? 0;
                return (
                    <Space direction="vertical" size={0}>
                        <Text>
                            {statements ?? row.callsMade} / {row.callsBudget}
                        </Text>
                        {gate > 0 && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                +{gate} gate
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: (
                <Space size={4}>
                    Cost
                    <Tooltip title="Tokens plus server-side web search. Search is billed per request on top of tokens and appears nowhere in token accounting, so a token-only figure understates a searching read.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </Space>
            ),
            key: 'cost',
            width: 150,
            render: (_: unknown, row: ModelBeliefsRunSummary) => {
                const cost = costs[row.id];
                if (!cost) {
                    return (
                        <Button
                            size="small"
                            type="link"
                            icon={<DollarOutlined style={{ marginRight: 4 }} />}
                            onClick={() => loadCost(row.id)}
                        >
                            Show
                        </Button>
                    );
                }
                return (
                    <Space direction="vertical" size={0}>
                        <Text strong>{usd(cost.totalCostUsd)}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {usd(cost.tokenCostUsd)} tokens
                            {cost.searchRequests > 0 &&
                                ` + ${usd(cost.searchCostUsd)} search`}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: (
                <Space size={4}>
                    Precision
                    <Tooltip title="Typical width of a searched cell's 95% interval, as the median half-width over cells where the models actually disagreed. Lower is better; the target is shown beneath. Cells where every answer was identical are counted separately as unanimous — a zero-width interval is agreement, not precision, and counting it as a hit rewards taking fewer samples. Searched only, because the memory pass is more self-consistent and mixing the two flatters the number. A run with top-up off is deliberately cheaper and less precise, which is what the tag says.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </Space>
            ),
            key: 'precision',
            width: 190,
            render: (_: unknown, row: ModelBeliefsRunSummary) => {
                if (!row.precision || row.precision.cells === 0) {
                    return <Text type="secondary">-</Text>;
                }
                const { met, varied, unanimous, target, medianHalfWidth } = row.precision;
                return (
                    <Space direction="vertical" size={0}>
                        <Space size={4}>
                            <Text strong>
                                {medianHalfWidth === null ? '-' : `±${medianHalfWidth}`}
                            </Text>
                            <Tag color={row.adaptiveTopUp === false ? 'orange' : 'green'}>
                                {row.adaptiveTopUp === false ? 'top-up off' : 'top-up on'}
                            </Tag>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            median of {varied} varied cells, target ±{target}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {met} met · {unanimous} unanimous
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Source',
            dataIndex: 'triggerSource',
            key: 'triggerSource',
            width: 110,
            render: (source: string) => (
                <Tag color={source === 'admin' ? 'default' : 'blue'}>{source}</Tag>
            ),
        },
        {
            title: 'Completed',
            dataIndex: 'completedAt',
            key: 'completedAt',
            render: (value: string | null, row: ModelBeliefsRunSummary) => (
                <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 12 }}>{when(value)}</Text>
                    {row.note && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.note}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_: unknown, row: ModelBeliefsRunSummary) => {
                if (['QUEUED', 'SAMPLING', 'GATING', 'AGGREGATING'].includes(row.status)) {
                    return (
                        <Button
                            size="small"
                            danger
                            icon={<StopOutlined style={{ marginRight: 4 }} />}
                            onClick={() => cancel(row.id)}
                        >
                            Stop
                        </Button>
                    );
                }
                if (row.status === 'READY') {
                    return (
                        <Space size={4}>
                            <ModelBeliefsReadPreview runId={row.id} />
                            {row.adHoc && (
                            <Tooltip title="The target this run was read from: category, competitors, price positioning. Edit it, then write the read again from the preview.">
                                <Button
                                    size="small"
                                    icon={<EditOutlined style={{ marginRight: 4 }} />}
                                    onClick={() => void editTarget(row)}
                                >
                                    Edit target
                                </Button>
                            </Tooltip>
                            )}
                            {row.adHoc && (
                            <Tooltip title="The brand has signed up: make this its first read. It shows on The Machines and in the brand's history.">
                                <Button
                                    size="small"
                                    icon={<LinkOutlined style={{ marginRight: 4 }} />}
                                    onClick={() => {
                                        setAttachBrand(null);
                                        setAttaching(row);
                                    }}
                                >
                                    Attach to brand
                                </Button>
                            </Tooltip>
                            )}
                        </Space>
                    );
                }
                return null;
            },
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={16}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Runs started today"
                            value={runs?.startedToday ?? 0}
                            suffix={`/ ${runs?.dailyCap ?? 0}`}
                            valueStyle={
                                runs?.capReached ? { color: '#ff4d4f' } : undefined
                            }
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic title="In flight" value={live.length} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                        <Space direction="vertical" size={0}>
                            <Text type="secondary">Panel</Text>
                            <Space wrap>
                                {(panel?.members ?? []).map((member) => (
                                    <Tag
                                        key={`${member.provider}:${member.model}`}
                                        color={
                                            member.warnings.length > 0
                                                ? 'red'
                                                : 'blue'
                                        }
                                    >
                                        {member.label}
                                        {member.batchSize
                                            ? ` · batch ${member.batchSize}`
                                            : ''}
                                        {member.readCompetitors === false
                                            ? ' · brand only'
                                            : ''}
                                    </Tag>
                                ))}
                            </Space>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {runs?.nextResumeAt && (
                <Alert
                    type="info"
                    showIcon
                    message="A run is waiting on a provider's daily quota"
                    description={`It resumes by itself after ${when(
                        runs.nextResumeAt,
                    )}. Work already paid for is not paid for again: completed batches are skipped on resume.`}
                />
            )}

            {(panel?.members ?? []).some((m) => m.warnings.length > 0) && (
                <Alert
                    type="warning"
                    showIcon
                    message="Panel warnings"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {(panel?.members ?? []).flatMap((m) =>
                                m.warnings.map((w) => (
                                    <li key={`${m.model}-${w}`}>
                                        <Text strong>{m.label}:</Text> {w}
                                    </li>
                                )),
                            )}
                        </ul>
                    }
                />
            )}

            <Card
                title={
                    <Space>
                        <SearchOutlined style={{ marginRight: 8 }} />
                        Runs
                    </Space>
                }
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                            onClick={() => void load()}
                            loading={loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                            onClick={() => {
                                setPickedBrand(null);
                                setPickingBrand(true);
                            }}
                        >
                            New run
                        </Button>
                        <Button
                            icon={<GlobalOutlined style={{ marginRight: 4 }} />}
                            onClick={() => setAdhocOpen(true)}
                        >
                            Ad-hoc read
                        </Button>
                    </Space>
                }
                styles={{ body: { paddingTop: 0 } }}
            >
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={runs?.runs ?? []}
                    columns={columns as any}
                    pagination={false}
                    locale={{
                        emptyText: <Empty description="No runs yet" />,
                    }}
                    expandable={{
                        onExpand: (expanded, row) => {
                            if (expanded) void loadCost(row.id);
                        },
                        expandedRowRender: (row: ModelBeliefsRunSummary) => {
                            const cost = costs[row.id];
                            if (!cost) return <Skeleton active paragraph={{ rows: 2 }} />;
                            return (
                                <Space
                                    direction="vertical"
                                    size={12}
                                    style={{ width: '100%' }}
                                >
                                    <Descriptions size="small" column={4} bordered>
                                        <Descriptions.Item label="Tokens">
                                            {cost.tokens.toLocaleString()}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="LLM calls">
                                            {cost.requests}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Web searches">
                                            {cost.searchRequests}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Total">
                                            <Text strong>{usd(cost.totalCostUsd)}</Text>
                                        </Descriptions.Item>
                                    </Descriptions>

                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="featureType"
                                        dataSource={cost.byFeature}
                                        columns={[
                                            {
                                                title: 'What it bought',
                                                dataIndex: 'featureType',
                                                render: (value: string) =>
                                                    value.replace(
                                                        'model_beliefs_ai_',
                                                        '',
                                                    ),
                                            },
                                            { title: 'Calls', dataIndex: 'requests' },
                                            {
                                                title: 'Tokens',
                                                dataIndex: 'tokens',
                                                render: (v: number) =>
                                                    v.toLocaleString(),
                                            },
                                            {
                                                title: 'Searches',
                                                dataIndex: 'searchRequests',
                                            },
                                        ]}
                                    />

                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="provider"
                                        dataSource={cost.byProvider}
                                        columns={[
                                            { title: 'Provider', dataIndex: 'provider' },
                                            { title: 'Calls', dataIndex: 'requests' },
                                            {
                                                title: 'Searches',
                                                dataIndex: 'searchRequests',
                                            },
                                            {
                                                title: 'Cost',
                                                dataIndex: 'costUsd',
                                                render: (v: number) => usd(v),
                                            },
                                        ]}
                                    />

                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="target"
                                        dataSource={cost.byTarget}
                                        columns={[
                                            { title: 'Target', dataIndex: 'target' },
                                            {
                                                title: 'Kind',
                                                dataIndex: 'targetKind',
                                                render: (k: string) => <Tag>{k}</Tag>,
                                            },
                                            { title: 'Calls', dataIndex: 'requests' },
                                            {
                                                title: 'Cost',
                                                dataIndex: 'costUsd',
                                                render: (v: number) => usd(v),
                                            },
                                        ]}
                                    />

                                    <Table
                                        size="small"
                                        pagination={false}
                                        rowKey="retrievalMode"
                                        dataSource={cost.byMode}
                                        columns={[
                                            {
                                                title: 'Retrieval',
                                                dataIndex: 'retrievalMode',
                                                render: (m: string) => (
                                                    <Tag color={m === 'on' ? 'blue' : 'default'}>
                                                        {m === 'on' ? 'searched' : 'from memory'}
                                                    </Tag>
                                                ),
                                            },
                                            { title: 'Calls', dataIndex: 'requests' },
                                            {
                                                title: 'Cost',
                                                dataIndex: 'costUsd',
                                                render: (v: number) => usd(v),
                                            },
                                        ]}
                                    />

                                    {(cost.searchRate ?? []).length > 0 && (
                                        <Table
                                            size="small"
                                            pagination={false}
                                            rowKey="model"
                                            dataSource={cost.searchRate}
                                            title={() => <Text strong>Searched when asked</Text>}
                                            columns={[
                                                { title: 'Model', dataIndex: 'model' },
                                                { title: 'Provider', dataIndex: 'provider' },
                                                { title: 'Search on', dataIndex: 'asked' },
                                                { title: 'Searched', dataIndex: 'searched' },
                                                {
                                                    title: 'Rate',
                                                    dataIndex: 'rate',
                                                    render: (v: number) => `${Math.round(v * 100)}%`,
                                                },
                                            ]}
                                        />
                                    )}

                                    {row.lastError && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message="This run is degraded"
                                            description={row.lastError}
                                        />
                                    )}

                                    <Paragraph
                                        type="secondary"
                                        style={{ fontSize: 12, marginBottom: 0 }}
                                    >
                                        {cost.caveats.join(' ')}
                                    </Paragraph>

                                    <ModelBeliefsReadPreview runId={row.id} />

                                    <ModelBeliefsRunDetail runId={row.id} />
                                </Space>
                            );
                        },
                    }}
                />
            </Card>

            <Modal
                open={pickingBrand}
                title="Run a read"
                okText="Next"
                okButtonProps={{ disabled: !pickedBrand }}
                onOk={() => {
                    if (!pickedBrand) return;
                    setPickingBrand(false);
                    setRunTarget({ kind: 'brand', brandId: pickedBrand.brandId, name: pickedBrand.name });
                }}
                onCancel={() => setPickingBrand(false)}
                destroyOnClose
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Brands with a category label. A brand without one is configured on the Brands tab
                        first.
                    </Text>
                    <ModelBeliefsBrandPicker
                        autoFocus
                        readableOnly
                        value={pickedBrand?.brandId ?? null}
                        onChange={(brandId, row) =>
                            setPickedBrand(brandId && row ? { brandId, name: row.brandName } : null)
                        }
                    />
                </Space>
            </Modal>

            <ModelBeliefsRunModal
                open={!!runTarget}
                target={runTarget}
                onClose={() => setRunTarget(null)}
                onStarted={() => void load()}
            />

            <Drawer
                width={880}
                open={adhocOpen}
                destroyOnClose
                styles={{ body: { textAlign: 'left' } }}
                onClose={() => setAdhocOpen(false)}
                title="Read a brand not on the platform"
            >
                <ModelBeliefsAdhocForm onStarted={() => void load()} />
            </Drawer>

            <Drawer
                width={880}
                open={!!editingTarget}
                destroyOnClose
                styles={{ body: { textAlign: 'left' } }}
                onClose={() => setEditingTarget(null)}
                title={editingTarget ? `${editingTarget.name} · ad-hoc target` : ''}
            >
                {editingTarget && (
                    <ModelBeliefsBrandConfigForm
                        configId={editingTarget.configId}
                        brandName={editingTarget.name}
                        onSaved={() => void load()}
                    />
                )}
            </Drawer>

            <Modal
                open={!!attaching}
                title={attaching ? `Attach run #${attaching.id} to a brand` : ''}
                okText="Attach"
                okButtonProps={{ disabled: !attachBrand, loading: attachBusy }}
                onOk={() => void attach()}
                onCancel={() => setAttaching(null)}
                destroyOnClose
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {attaching?.targetName ? `The read of ${attaching.targetName}` : 'This read'} becomes
                        the brand&rsquo;s first read: it shows on The Machines and in the brand&rsquo;s
                        history, and the brand&rsquo;s customers can see it. If the brand has no Model
                        Beliefs config yet, one is created from the ad-hoc form so the monthly refresh reads
                        the same thing.
                    </Text>
                    <ModelBeliefsBrandPicker
                        autoFocus
                        value={attachBrand?.brandId ?? null}
                        onChange={(brandId, row) =>
                            setAttachBrand(brandId && row ? { brandId, name: row.brandName } : null)
                        }
                    />
                </Space>
            </Modal>
        </Space>
    );
};
